import { Injectable } from '@angular/core';
import { Subject } from 'rxjs/Rx';
import { NotificationsService } from 'angular2-notifications';

import { DadosVestibular } from 'models/dados-vestibular';

@Injectable()
export class FluxoVestibularService {

  passoAtual = 1;
  dadosVestibular: DadosVestibular = new DadosVestibular({});
  private resumoFluxo: any[];

  // Observable string sources
  private dadosVestibularMessage = new Subject<any>();
  private resumoFluxoMessage = new Subject<any[]>();

  // Observable string streams
  dadosVestibularMessage$ = this.dadosVestibularMessage.asObservable();
  resumoFluxoMessage$ = this.resumoFluxoMessage.asObservable();

  constructor(
    private notificationsService: NotificationsService,
  ) { }

  getPassoAtual(): number {
    return this.passoAtual;
  }

  private setPassoAtual(passoAtual: number): void {
    this.passoAtual = passoAtual;
  }

  getDadosVestibular(): DadosVestibular {
    return this.dadosVestibular;
  }

  setDadosVestibular(payload: any): void {
    this.dadosVestibular = { ...this.dadosVestibular, ...payload };
    this.dadosVestibularMessage.next(this.dadosVestibular);
    this.atualizaResumoFluxo();
  }

  proximoPasso(payload?: any): void {
    this.setPassoAtual(this.passoAtual + 1);
    if (payload) {
      this.setDadosVestibular(payload);
    }
  }

  voltarPasso(): void {
    if (this.passoAtualRetornavel()) {
      this.setPassoAtual(this.passoAtual - 1 <= 0 ? 1 : this.passoAtual - 1);
      this.atualizaResumoFluxo();
    }
  }

  passoAtualRetornavel(): boolean {
    const fluxo = this.getResumoFluxo();
    if (fluxo && fluxo.length > 0 && this.passoAtual > fluxo[0].id) {
      // Verifica se existe algum passo anterior que seja final
      const passosFinais = fluxo.filter(p => p.id < this.passoAtual && p.final);
      return passosFinais.length === 0 && this.podeAlterarDadosVestibular();
    }
    return false;
  }

  podeAlterarDadosVestibular() {
    return !(this.dadosVestibular && this.dadosVestibular.agendamento && this.dadosVestibular.agendamento.numeroInscricao);
  }

  atualizarPassoPorRota(rotaAtual: string) {
    const passo = this.getResumoFluxo().filter(passoFluxo => passoFluxo.rota === rotaAtual);
    if (passo.length > 0) {
      this.setPassoAtual(passo[0].id);
      this.atualizaResumoFluxo();
    } else {
      this.notificationsService.error('Ação não permitida!', 'Para completar a inscrição você precisa passar por cada etapa na ordem estabelecida.');
    }
  }

  proximaRota(): string {
    // usar sempre depois de proximoPasso, pois depende do incremento da var passoAtual
    const proximoPasso = this.getResumoFluxo().filter(passo => passo.id === this.passoAtual);
    if (proximoPasso.length <= 0) {
      return null;
    }
    return proximoPasso[0].rota;
  }

  rotaAnterior(): string {
    const passoAnterior = this.getResumoFluxo().filter(passo => passo.id === this.passoAtual);
    if (passoAnterior.length <= 0) {
      return null;
    }
    return passoAnterior[0].rota;
  }

  validarRotaAtual(rota: string): boolean {
    const rotaValida = this.getResumoFluxo()
      .filter(passo => passo.id === this.getPassoAtual() && passo.rota === rota).length > 0;
    return rotaValida;
  }

  // ESSE CÓDIGO ABAIXO PREVE A POSSIBILIDADE DO USUÁRIO NAVEGAR ENTRE AS ETAPAS DO FLUXO
  // POR ISSO MANTENHO AQUI, COMENTADO, PARA QUE POSSAMOS UTILIZAR NO FUTURO.
  //
  // validarRotaAtual(rota: string): boolean {
  //   // para ser uma rota válida, deve ser igual a etapa atual ou
  //   // qualquer outra etapa que já tenha sido preenchida antes da atual
  //   const passosPermitidos = this.getResumoFluxo()
  //     .filter(passo => passo.id === this.passoAtual || passo.active);

  //   // se a rota for valida, deve ajustar o passoAtual de acordo com a rota.
  //   // o usuário pode simplesmente estar navegando
  //   // entre as etapas já preenchidas, deve prever esse caso.
  //   const rotaValida = passosPermitidos.filter(passo => passo.rota === rota).length > 0;
  //   if (rotaValida) {
  //     const passoCorrigido = passosPermitidos.filter(passo => passo.rota === rota)[0].id;
  //     this.setPassoAtual(passoCorrigido);
  //     this.atualizaResumoFluxo();
  //   }
  //   // console.log(`passoAtual: ${this.passoAtual}`);
  //   // console.log(this.getResumoFluxo());
  //   return rotaValida;
  // }

  getResumoFluxo(): any[] {
    if (!this.resumoFluxo || this.resumoFluxo.length === 0) {
      this.resumoFluxo = this.initResumoFluxo();
      // garante a correspondencia com passo e dados atuais
      this.atualizaResumoFluxo();
    }
    return this.resumoFluxo;
  }

  private initResumoFluxo(): any[] {
    if (!this.dadosVestibular || !this.dadosVestibular.vestibular || !this.dadosVestibular.vestibular.tipo) {
      return [];
    }

    let passos = [];
    if (this.dadosVestibular.vestibular.isReingresso) {
      // Vestibular medicina EURO
      if (this.dadosVestibular.vestibular.codigo === 1 || this.dadosVestibular.vestibular.codigo === 2) {
        passos = [
          {
            id: 1,
            desc: 'Dados pessoais',
            active: false,
            current: true,
            rota: 'dados-pessoais',
            data: [],
          },
          {
            id: 2,
            desc: 'Sobre o curso',
            active: false,
            current: false,
            rota: 'sobre-o-curso',
            dadosCurso: true,
            data: [],
          },
          {
            id: 3,
            desc: 'Agendamento',
            active: false,
            current: false,
            rota: 'agendamento',
            final: true,
            data: [],
          },
          {
            id: 4,
            desc: 'Dados de pagamento',
            active: false,
            current: false,
            rota: 'pagamento',
            data: [],
          },
          {
            id: 5,
            desc: 'Finalização',
            active: false,
            current: false,
            rota: 'finalizacao',
            hide: true,
            data: [],
          },
        ];
      }
      else if(this.dadosVestibular.vestibular.sigla!='RM'){
        passos = [
          {
            id: 1,
            desc: 'Dados pessoais',
            active: false,
            current: true,
            rota: 'dados-pessoais',
            data: [],
          },
          {
            id: 2,
            desc: 'Sobre o curso',
            active: false,
            current: false,
            rota: 'sobre-o-curso',
            dadosCurso: true,
            data: [],
          },
          {
            id: 3,
            desc: 'Agendamento',
            active: false,
            current: false,
            rota: 'agendamento',
            final: true,
            data: [],
          },
          {
            id: 4,
            desc: 'Finalização',
            active: false,
            current: false,
            rota: 'finalizacao',
            hide: true,
            data: [],
          },
        ];
      }
      else{
        passos = [
          {
            id: 1,
            desc: 'Dados pessoais',
            active: false,
            current: true,
            rota: 'dados-pessoais',
            data: [],
          },
          {
            id: 2,
            desc: 'Sobre o curso',
            active: false,
            current: false,
            rota: 'sobre-o-curso',
            dadosCurso: true,
            data: [],
          },
          {
            id: 3,
            desc: 'Agendamento',
            active: false,
            current: false,
            rota: 'agendamento',
            final: true,
            data: [],
          },
          {
            id: 4,
            desc: 'Dados de pagamento',
            active: false,
            current: false,
            rota: 'pagamento',
            data: [],
          },
          {
            id: 5,
            desc: 'Finalização',
            active: false,
            current: false,
            rota: 'finalizacao',
            hide: false,
            data: [],
          },
        ];
      }
    } else if (this.dadosVestibular.vestibular.isEnem) {
      passos = [
        {
          id: 1,
          desc: 'Sobre o curso',
          active: false,
          current: true,
          rota: 'curso',
          dadosCurso: true,
          data: [],
        },
        {
          id: 2,
          desc: 'Dados pessoais',
          active: false,
          current: false,
          rota: 'dados-pessoais',
          data: [],
        },
        {
          id: 3,
          desc: 'Nota Enem',
          active: false,
          current: false,
          rota: 'nota-enem',
          final: true,
          data: [],
        },
        /*{
          id: 4,
          desc: 'Dados de pagamento',
          active: false,
          current: false,
          rota: 'pagamento',
          data: [],
        },*/
        {
          id: 4,
          desc: 'Finalização',
          active: false,
          current: false,
          rota: 'finalizacao',
          hide: true,
          data: [],
        },
      ];

    } else {
      if (this.dadosVestibular.vestibular.siglaVestibular === "CB") {
        passos = [
          {
            id: 1,
            desc: 'Dados pessoais',
            active: false,
            current: true,
            rota: 'dados-pessoais',
            data: [],
          },
          {
            id: 2,
            desc: 'Sobre o curso',
            active: false,
            current: false,
            rota: 'sobre-o-curso',
            dadosCurso: true,
            data: [],
          },
          {
            id: 3,
            desc: 'Finalização',
            active: false,
            current: false,
            rota: 'finalizacao',
            hide: true,
            data: [],
          },
        ];
      }
      // agendado é a base para os demais
      else if(this.dadosVestibular.vestibular.valorInscricao !== 0) {
        passos = [
          {
            id: 1,
            desc: 'Sobre o curso',
            active: false,
            current: true,
            rota: 'curso',
            dadosCurso: true,
            data: [],
          },
          {
            id: 2,
            desc: 'Dados pessoais',
            active: false,
            current: false,
            rota: 'dados-pessoais',
            data: [],
          },
          {
            id: 3,
            desc: 'Agendamento',
            active: false,
            current: false,
            rota: 'agendamento',
            final: true,
            data: [],
          },
          {
            id: 4,
            desc: 'Dados de pagamento',
            active: false,
            current: false,
            rota: 'pagamento',
            data: [],
          },
          {
            id: 5,
            desc: 'Finalização',
            active: false,
            current: false,
            rota: 'finalizacao',
            hide: true,
            data: [],
          },
        ];
      } else {
        passos = [
          {
            id: 1,
            desc: 'Sobre o curso',
            active: false,
            current: true,
            rota: 'curso',
            dadosCurso: true,
            data: [],
          },
          {
            id: 2,
            desc: 'Dados pessoais',
            active: false,
            current: false,
            rota: 'dados-pessoais',
            data: [],
          },
          {
            id: 3,
            desc: 'Agendamento',
            active: false,
            current: false,
            rota: 'agendamento',
            final: true,
            data: [],
          },
          {
            id: 4,
            desc: 'Finalização',
            active: false,
            current: false,
            rota: 'finalizacao',
            hide: true,
            data: [],
          },
        ];
      }

      switch (this.dadosVestibular.vestibular.tipo) {
        case 'medicina':
        case 'tradicional':
          passos[2].desc = 'Sobre a prova';
          passos[2].rota = 'sobre-a-prova';
          break;
      }

    }
    return passos;
  }

  atualizaResumoFluxo() {
    /* Verificando se existe sessionStorage com o valor do curso */
    if(sessionStorage.getItem("valorCurso")){
      this.dadosVestibular.valorCurso = sessionStorage.getItem("valorCurso");
    }
    //console.log(this.dadosVestibular);
    sessionStorage.removeItem("valorCurso");
    if (!this.resumoFluxo || this.resumoFluxo.length === 0) {
      return;
    }

    const passoReal = this.resumoFluxo.filter(passo => passo.current)[0].id;
    this.resumoFluxo.forEach(passo => {

      passo.current = this.getPassoAtual() === passo.id;
      if (passo.id < this.getPassoAtual()) {
        // seta o passo como ativo, caso já tenha sido preenchido
        passo.active = true;
      }

      // reingresso
      if (this.dadosVestibular.vestibular.isReingresso) {
        switch (passo.id) {
          case 1:
            if (passo.active) {
              passo.data = [
                { label: 'Nome: ', value: this.dadosVestibular.candidato.nome },
                { label: 'CPF: ', value: this.dadosVestibular.candidato.cpf },
              ];
            }
            break;

          case 2:
            if (passo.active) {
              passo.data = [
                { label: 'Campus: ', value: this.dadosVestibular.cursoSelecionado.campusTurno.nome },
                { label: 'Turno: ', value: this.getTurnoDesc(this.dadosVestibular.cursoSelecionado.campusTurno.turno) },
                { label: 'Referência: ', value: this.dadosVestibular.candidato.dadosCpd.anoSemestreRef },
              ];
            }
            break;

          case 3:
            if (passo.active) {
              let local = '';
              if (this.dadosVestibular.agendamento.local) {
                local = `${this.dadosVestibular.agendamento.local.descricao}, ${this.dadosVestibular.agendamento.local.predioSala}`;
              }

              // Vestibular Medicina EURO
              if (this.dadosVestibular.vestibular.codigo === 1) {
                passo.data = [
                  { label: 'Prova: ', value: this.dadosVestibular.agendamento ? this.dadosVestibular.agendamento.data.descricao : 'Não informado!' },
                  { label: 'Local da prova: ', value: `${this.dadosVestibular.agendamento.local.descricao}` },
                ];
              } else if (this.dadosVestibular.vestibular.codigo === 2) {
                passo.data = [];
              }
              else {
                passo.data = [
                  { label: 'Prova: ', value: this.dadosVestibular.agendamento ? this.dadosVestibular.agendamento.data.descricao : 'Não informado!' },
                  { label: 'Local da prova: ', value: `Campus ${local}` },
                ];
              }
            }
            break;
        }

      } else {
        switch (passo.id) {
          case 1:
            if (passo.active) {
              let turno;
              if(this.dadosVestibular.cursoSelecionado.campusTurno.turno.turno==undefined){
                turno = this.dadosVestibular.cursoSelecionado.campusTurno.turno;
              }else{
                turno = this.dadosVestibular.cursoSelecionado.campusTurno.turno.turno;
              }
              passo.data = [
                { label: 'Campus: ', value: this.dadosVestibular.cursoSelecionado.campusTurno.nome },
                { label: 'Turno: ', value: this.getTurnoDesc(turno) },
              ];
            }
            break;

          case 2:
            if (passo.active) {
              passo.data = [
                { label: 'Nome: ', value: this.dadosVestibular.candidato.nome },
                { label: 'CPF: ', value: this.dadosVestibular.candidato.cpf },
              ];
            }
            break;

          case 3:
            if (passo.active && this.dadosVestibular.vestibular.sigla!='MN') {
              let local = '';
              if (this.dadosVestibular.agendamento && this.dadosVestibular.agendamento.local) {
                local = `${this.dadosVestibular.agendamento.local.descricao}, ${this.dadosVestibular.agendamento.local.predioSala}`;
              }
              /* TODO - Aqui está dando erro na hora de buscar data de prova do vestibular de medicina
                 Coloquei somente a data, sem hora e sem o local da prova
              */
              if (this.dadosVestibular.agendamento && this.dadosVestibular.agendamento.data) {
                if(this.dadosVestibular.vestibular.sigla!='ME'&&this.dadosVestibular.vestibular.sigla!== 'MN'&&this.dadosVestibular.vestibular.sigla!== 'TE'){
                  passo.data = [
                    { label: 'Prova: ', value: this.dadosVestibular.agendamento ? this.dadosVestibular.agendamento.data.descricao : 'Não informado!' },
                    { label: 'Local da prova: ', value: `Campus ${local}` },
                  ];
                }else{
                  if(this.dadosVestibular.vestibular.sigla!== 'TE'){
                    passo.data = [
                      { label: 'Prova: ', value: this.dadosVestibular.agendamento ? this.dadosVestibular.agendamento.data.descricao : 'Não informado!'}
                    ]
                  }
                }
              }

              if (this.dadosVestibular.vestibular.sigla==='CB') {
                passo.data = []
              }

              if (this.dadosVestibular.enem && this.dadosVestibular.vestibular.sigla=="EN") {
                passo.data = [
                  { label: 'Nota do ENEM: ', value: this.dadosVestibular.enem.pontuacaoEnem },
                  { label: 'Ano ENEM: ', value: this.dadosVestibular.enem.anoEnem },
                ];
              }
            }
            break;

          case 4:
            if (passo.active) {
              passo.data = [
                { label: 'Pagamento: ', value: this.dadosVestibular.pagamento.formaPagamento.nome },
              ];
            }
            break;
        }

        // ead
        if (this.dadosVestibular.vestibular.isEad) {
          if (passo.id === 1 && passo.active) {
            passo.data = [
              { label: 'Modalidade: ', value: 'Ensino à distância' },
              { label: 'Cidade: ', value: this.dadosVestibular.cursoSelecionado.polo.nome },
              { label: 'Apoio: ', value: this.dadosVestibular.cursoSelecionado.campusTurno.nome },
            ];
          }
        }

        // medicina e tradicional
        switch (this.dadosVestibular.vestibular.tipo) {
          case 'medicina':
          case 'tradicional':
            if (passo.id === 2 && passo.active) {
              passo.data[1].value = 'Disponibilizado no cartão de inscrição';
            }
            break;
        }
      }
    });
    this.resumoFluxoMessage.next(this.resumoFluxo);
  }

  getTurnoDesc(turno) {
    //console.log(turno);
    let retorno = '';
    switch (turno) {
      case 'I':
        retorno = 'Integral';
        break;
      case 'M':
        retorno = 'Matutino';
        break;
      case 'V':
        retorno = 'Vespertino';
        break;
      case 'N':
        retorno = 'Noturno';
        break;
    }

    return retorno;
  }
}
